/**
 * App shell — the loop, wired.
 *
 * The screen graph is deliberately shaped like the pitch's flywheel, and every
 * edge in it is one tap:
 *
 *   home ──raid──▶ result ──"改一手"──▶ build ──publish──▶ share link
 *     ▲              │                                        │
 *     └──inbox◀──reaction filed──◀ replay ◀──"看看昨晚"────────┘
 *
 * Nothing here is allowed to dead-end. A failed raid offers revenge; a watched
 * reaction offers an edit; a published room offers a link. If you find a screen
 * whose only exit is "back", that is a bug in the product, not a missing button.
 */

import { roomFromHash, roomShareUrl } from '../core/codec'
import type { RoomDef } from '../core/room'
import { cloneRoom, emptyRoom } from '../core/room'
import { seedFromString } from '../core/rng'
import { api } from '../net/api'
import { platform } from '../platform/crazygames'
import { RoomView } from '../render/roomView'
import { SceneView } from '../render/scene'
import { disposePropGeometries } from '../render/geometry'
import { disposeToonCaches } from '../render/toon'
import { initPhysics, physicsReady } from '../sim/raid'
import { preloadCharacter } from '../render/character'
import { sfx } from '../audio/sfx'
import { digestReplay, type Replay } from '../sim/replay'
import { button, copyText, el, toast } from '../ui/dom'
import { BuildMode } from './buildMode'
import { demoRoom, DEMO_ROOMS } from './demoRooms'
import { RaidMode } from './raidMode'
import { ReplayMode } from './replayMode'
import {
  loadInbox,
  loadLastRaided,
  loadMyRoom,
  loadPlayer,
  markInboxSeen,
  pushInbox,
  rememberRaided,
  savePlayer,
  saveMyRoom,
  unseenCount,
} from './store'

const soundLabel = (): string => (sfx.isMuted ? '🔇 声音关' : '🔊 声音开')

type Mode =
  | { kind: 'none' }
  | { kind: 'build'; mode: BuildMode }
  | { kind: 'raid'; mode: RaidMode }
  | { kind: 'replay'; mode: ReplayMode }

export class App {
  private scene: SceneView
  private uiHost: HTMLElement
  private current: Mode = { kind: 'none' }
  private overlay: HTMLDivElement
  private clock = 0
  private last = 0
  private running = true
  private invited: RoomDef | null = null
  private remotePool: RoomDef[] = []
  private backdrop: RoomView | null = null

  constructor(stage: HTMLElement, uiHost: HTMLElement) {
    this.scene = new SceneView(stage)
    this.uiHost = uiHost
    this.overlay = el('div', { class: 'overlay' })
    uiHost.appendChild(this.overlay)
  }

  async boot(): Promise<void> {
    this.showLoading('正在打开玩具屋…')
    await platform.init()
    // Start the physics download but do not wait for it: the menu, the invite
    // card and the whole builder work without it, and on a phone that is the
    // difference between "instant" and "another 800 KB first".
    void initPhysics()
    // Same deal for the character: the figure falls back to the procedural toy, so this download
    // overlaps with the player reading the menu instead of delaying it.
    void preloadCharacter()

    this.invited = roomFromHash()
    const invitedId = platform.invitedRoom()
    if (!this.invited && invitedId) {
      const summary = await api.fetchRoom(invitedId)
      if (summary?.room) this.invited = summary.room
    }

    // Warm the stranger pool in the background; the menu never waits on it.
    void api.listRooms(12).then((rooms) => {
      this.remotePool = rooms.map((r) => r.room).filter((r): r is RoomDef => r !== null)
    })

    this.showHome()
    this.last = performance.now()
    requestAnimationFrame(this.frame)
  }

  // ------------------------------------------------------------------ screens

  private clearMode(): void {
    if (this.current.kind !== 'none') this.current.mode.dispose()
    this.current = { kind: 'none' }
    this.clearBackdrop()
  }

  private showLoading(text: string): void {
    this.overlay.textContent = ''
    this.overlay.appendChild(el('div', { class: 'loading', text }))
  }

  private showHome(): void {
    this.clearMode()
    this.scene.setMode('overview')
    this.overlay.textContent = ''

    const player = loadPlayer()
    const myRoom = loadMyRoom()
    const unseen = unseenCount()

    const nameInput = el('input', {
      class: 'name-input',
      type: 'text',
      maxlength: '18',
      placeholder: '你的名字（会出现在别人的收件箱里）',
      value: player.name,
    }) as HTMLInputElement
    nameInput.addEventListener('change', () => {
      savePlayer({ name: nameInput.value.trim().slice(0, 18) })
    })

    const cards: HTMLElement[] = []

    if (this.invited) {
      // The invited path is first and loudest: someone dared you, and the whole
      // acquisition story depends on this being a single tap.
      const r = this.invited
      cards.push(
        el('div', { class: 'card card-invite' }, [
          el('div', { class: 'card-kicker', text: `${r.ownerName} 叫你来试试` }),
          el('div', { class: 'card-title', text: r.name }),
          el('div', { class: 'card-taunt', text: r.taunt || '（他什么都没说。）' }),
          button('闯进去', () => this.startRaid(cloneRoom(r)), 'btn primary big'),
        ]),
      )
    }

    const strangerRoom = (): RoomDef => {
      if (this.remotePool.length > 0) {
        // Deterministic pick: the seed is the day, so "today's room" is a thing
        // you can talk about with a friend.
        const day = Math.floor(Date.now() / 86_400_000)
        const idx = seedFromString(`pool-${day}`) % this.remotePool.length
        return cloneRoom(this.remotePool[idx]!)
      }
      const idx = Math.floor(this.clock) % DEMO_ROOMS.length
      return cloneRoom(demoRoom(idx))
    }

    cards.push(
      el('div', { class: 'card' }, [
        el('div', { class: 'card-title', text: '闯一间别人的屋子' }),
        el('div', {
          class: 'card-body',
          text: '进去拿宝箱，在门锁上之前出来。屋主的机关不会告诉你它在哪。',
        }),
        el('div', { class: 'card-row' }, [
          button('随便来一间', () => this.startRaid(strangerRoom()), 'btn primary'),
          ...DEMO_ROOMS.map((r) =>
            button(r.name, () => this.startRaid(cloneRoom(r)), 'btn ghost'),
          ),
        ]),
      ]),
      el('div', { class: 'card' }, [
        el('div', { class: 'card-title', text: '布置你自己的屋子' }),
        el('div', {
          class: 'card-body',
          text: myRoom
            ? '改一手，再发出去。你不在线的时候，它继续接待陌生人。'
            : '把宝箱放在一个诱人的地方，然后想清楚：他会怎么走？',
        }),
        el('div', { class: 'card-row' }, [
          button(myRoom ? '继续布置' : '开始布置', () => this.startBuild(myRoom), 'btn primary'),
          myRoom ? button('分享我的屋子', () => void this.share(myRoom), 'btn ghost') : null,
        ].filter(Boolean) as HTMLElement[]),
      ]),
    )

    const lastRaided = loadLastRaided()
    if (lastRaided) {
      cards.push(
        el('div', { class: 'card card-thin' }, [
          el('div', { class: 'card-title', text: `再去一次「${lastRaided.name}」` }),
          button('复仇', () => this.startRaid(cloneRoom(lastRaided)), 'btn ghost'),
        ]),
      )
    }

    const inboxBtn = el('button', { class: 'btn ghost inbox-btn', type: 'button' }, [
      el('span', { text: '收件箱：谁闯进来了' }),
      unseen > 0 ? el('span', { class: 'badge', text: String(unseen) }) : null,
    ].filter(Boolean) as HTMLElement[])
    inboxBtn.addEventListener('click', () => this.showInbox())

    // Sound toggle. Lives on the home screen rather than in the raid HUD because a portal player
    // decides once, and the raid screen's job is to stay out of the way of the room.
    const soundBtn = button(soundLabel(), () => {
      sfx.toggleMuted()
      soundBtn.textContent = soundLabel()
    }, 'btn ghost small sound-btn')

    this.overlay.appendChild(
      el('div', { class: 'home' }, [
        el('div', { class: 'brand' }, [
          el('h1', { text: 'ToyRaiders' }),
          el('p', { text: '别人的玩具屋，别人的坏心眼。' }),
        ]),
        nameInput,
        ...cards,
        inboxBtn,
        soundBtn,
      ]),
    )

    this.showBackdrop(this.invited ?? myRoom ?? demoRoom(0))
  }

  /**
   * Menu/result backdrop: the real `RoomView`, so there is exactly one art path
   * in the codebase and the menu can never show a room the game can't run.
   */
  private showBackdrop(room: RoomDef): void {
    this.backdrop?.dispose()
    this.backdrop = new RoomView(room)
    this.scene.scene.add(this.backdrop.root)
    this.scene.setRoomSize(room.width, room.depth)
    this.scene.setMode('overview')
  }

  private clearBackdrop(): void {
    this.backdrop?.dispose()
    this.backdrop = null
  }

  private startBuild(room: RoomDef | null): void {
    this.clearMode()
    this.overlay.textContent = ''
    const player = loadPlayer()
    const base =
      room ??
      (() => {
        const fresh = emptyRoom(seedFromString(`${player.name}-${Date.now()}`), player.name || '匿名屋主')
        return fresh
      })()
    const mode = new BuildMode({
      scene: this.scene,
      uiHost: this.uiHost,
      room: { ...base, ownerName: player.name || base.ownerName || '匿名屋主' },
      onPlaytest: (r) => {
        saveMyRoom(r)
        this.startRaid(r, { playtest: true })
      },
      onPublish: (r) => {
        saveMyRoom(r)
        void this.share(r)
      },
      onExit: () => {
        saveMyRoom(mode.currentRoom)
        this.showHome()
      },
    })
    this.current = { kind: 'build', mode }
  }

  private startRaid(room: RoomDef, opts: { playtest?: boolean } = {}): void {
    if (!physicsReady()) {
      // First raid of the session: the wasm may still be in flight.
      this.clearMode()
      this.showLoading('正在搬进机关…')
      void initPhysics().then(() => this.startRaid(room, opts))
      return
    }
    this.clearMode()
    this.overlay.textContent = ''
    rememberRaided(room)
    const player = loadPlayer()
    const mode = new RaidMode({
      scene: this.scene,
      uiHost: this.uiHost,
      room,
      raiderName: player.name,
      playtest: opts.playtest,
      onFinish: (outcome, replay) => this.showResult(room, outcome, replay, opts.playtest ?? false),
      onQuit: () => this.showHome(),
    })
    this.current = { kind: 'raid', mode }
  }

  private showResult(
    room: RoomDef,
    outcome: 'escaped' | 'failed',
    replay: Replay,
    playtest: boolean,
  ): void {
    const digest = digestReplay(replay)

    // File the reaction. Against the live API it goes to the room's owner; with
    // no server it lands in your own inbox so the loop is still visible.
    void api.submitReplay(room.id, replay)
    if (!playtest) pushInbox(replay)

    const lines: string[] = []
    if (digest.launches > 0) lines.push(`被弹飞 ${digest.launches} 次`)
    if (digest.pitfalls > 0) lines.push(`掉洞 ${digest.pitfalls} 次`)
    if (digest.hesitations.length > 0) lines.push(`犹豫 ${digest.hesitations.length} 次`)
    if (digest.fellForDecoy) lines.push('先跑向了假宝箱')
    if (digest.respectedBluff) lines.push('绕开了那块假陷阱')
    lines.push(`${digest.seconds.toFixed(1)} 秒`)

    this.overlay.textContent = ''
    this.overlay.appendChild(
      el('div', { class: 'result' }, [
        el('div', {
          class: `result-title ${outcome}`,
          text: outcome === 'escaped' ? '带出去了' : '空手出来了',
        }),
        el('div', { class: 'result-sub', text: digest.headline }),
        el(
          'div',
          { class: 'replay-chips' },
          lines.map((l) => el('span', { class: 'stat-chip', text: l })),
        ),
        el('div', { class: 'result-cta' }, [
          button('再来一次', () => this.startRaid(cloneRoom(room), { playtest }), 'btn primary'),
          button('看这一遍', () => this.showReplay(replay), 'btn'),
          button('把这间屋子甩给朋友', () => void this.share(room), 'btn ghost'),
          button('去布置我的屋子', () => this.startBuild(loadMyRoom()), 'btn ghost'),
          button('回主菜单', () => this.showHome(), 'btn ghost small'),
        ]),
      ]),
    )
  }

  private showInbox(): void {
    this.clearMode()
    markInboxSeen()
    const inbox = loadInbox()
    this.overlay.textContent = ''

    const rows =
      inbox.length === 0
        ? [
            el('div', {
              class: 'empty',
              text: '还没有反应。发一个链接给朋友，或者先自己闯一间屋子试试。',
            }),
          ]
        : inbox.map((r) => {
            const d = digestReplay(r)
            const row = el('button', { class: 'inbox-row', type: 'button' }, [
              el('div', { class: 'inbox-headline', text: d.headline }),
              el('div', {
                class: 'inbox-meta',
                text: `${d.outcome === 'escaped' ? '成功' : '失手'} · ${d.seconds.toFixed(1)}s · 犹豫 ${d.hesitations.length} 次`,
              }),
            ])
            row.addEventListener('click', () => this.showReplay(r))
            return row
          })

    this.overlay.appendChild(
      el('div', { class: 'inbox' }, [
        el('div', { class: 'inbox-top' }, [
          button('← 返回', () => this.showHome(), 'btn ghost small'),
          el('div', { class: 'inbox-title', text: '谁闯进来了' }),
        ]),
        el('div', {
          class: 'inbox-note',
          text: '接上服务器后，这里是陌生人留下的反应；现在只有你自己的。',
        }),
        ...rows,
      ]),
    )
  }

  private showReplay(replay: Replay): void {
    this.clearMode()
    this.overlay.textContent = ''
    const mode = new ReplayMode({
      scene: this.scene,
      uiHost: this.uiHost,
      replay,
      onExit: () => this.showHome(),
      onEdit: (room) => this.startBuild(room),
      onRaid: (room) => this.startRaid(room),
    })
    this.current = { kind: 'replay', mode }
  }

  // -------------------------------------------------------------------- share

  private async share(room: RoomDef): Promise<void> {
    const published = await api.publishRoom(room)
    const fallback = roomShareUrl(room)
    const link = await platform.inviteLink({ room: room.id }, fallback)
    const text = `${room.taunt || '你绝对过不了'} — ${link}`

    if (navigator.share) {
      try {
        await navigator.share({ title: 'ToyRaiders', text: room.taunt || '你绝对过不了', url: link })
        return
      } catch {
        // User dismissed the sheet; fall through to clipboard.
      }
    }
    const copied = await copyText(text)
    toast(
      this.uiHost,
      copied
        ? published
          ? '链接已复制。发出去，然后等着看他怎么中招。'
          : '链接已复制（离线模式：整间屋子都在链接里）。'
        : link,
      3600,
    )
  }

  // --------------------------------------------------------------------- loop

  private frame = (now: number): void => {
    if (!this.running) return
    const dt = Math.min(0.05, (now - this.last) / 1000)
    this.last = now
    this.clock += dt

    switch (this.current.kind) {
      case 'build':
        this.current.mode.update(this.clock, dt)
        break
      case 'raid':
        this.current.mode.update(dt)
        break
      case 'replay':
        this.current.mode.update(dt)
        break
      default:
        this.backdrop?.update(this.clock)
        this.scene.update(dt, null)
        break
    }
    this.scene.render()
    requestAnimationFrame(this.frame)
  }

  dispose(): void {
    this.running = false
    this.clearMode()
    this.scene.dispose()
    disposePropGeometries()
    disposeToonCaches()
  }
}
